// Form per creazione/modifica sede
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { MapPin, Paperclip, Download, Trash2 } from 'lucide-react'
import { Modal } from '../../../components/ui/Modal'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { FileUpload } from '../../../components/shared/FileUpload'
import api from '../../../lib/api'

interface UploadedFile {
  file: File
  preview?: string
}

interface SiteDocMeta {
  id: string
  filename: string
  mimeType: string
  size: number
  createdAt: string
}

const siteSchema = z.object({
  name: z.string().min(1, 'Nome obbligatorio'),
  address: z.string().min(1, 'Indirizzo obbligatorio'),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  contactName: z.string().optional(),
  contactPhone: z.string().optional(),
  contactEmail: z.string().email('Email non valida').optional().or(z.literal('')),
  accessInstructions: z.string().optional(),
  notes: z.string().optional(),
  technicalNotes: z.string().optional(),
  equipmentComposition: z.string().optional(),
})

type SiteFormData = z.infer<typeof siteSchema>

export interface SiteData {
  id: string
  name: string
  address: string
  city?: string
  postalCode?: string
  latitude?: number | null
  longitude?: number | null
  contactName?: string | null
  contactPhone?: string | null
  contactEmail?: string | null
  accessInstructions?: string | null
  notes?: string | null
  technicalNotes?: string | null
  equipmentComposition?: unknown[]
}

interface SiteFormProps {
  companyId: string
  site?: SiteData | null
  open: boolean
  onClose: () => void
}

export function SiteForm({ companyId, site, open, onClose }: SiteFormProps) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(site?.id)
  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([])

  const equipmentToString = (eq: unknown[] | undefined): string => {
    if (!eq || eq.length === 0) return ''
    if (
      eq.length === 1 &&
      typeof (eq[0] as Record<string, unknown>)?.description === 'string' &&
      Object.keys(eq[0] as object).length === 1
    ) {
      return String((eq[0] as Record<string, unknown>).description)
    }
    return JSON.stringify(eq, null, 2)
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SiteFormData>({
    resolver: zodResolver(siteSchema),
    defaultValues: {
      name: '',
      address: '',
      city: '',
      postalCode: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      accessInstructions: '',
      notes: '',
      technicalNotes: '',
      equipmentComposition: '',
    },
  })

  // Reset con i dati della sede ogni volta che il modal si apre o cambia sede
  useEffect(() => {
    if (open) {
      reset({
        name: site?.name ?? '',
        address: site?.address ?? '',
        city: site?.city ?? '',
        postalCode: site?.postalCode ?? '',
        contactName: site?.contactName ?? '',
        contactPhone: site?.contactPhone ?? '',
        contactEmail: site?.contactEmail ?? '',
        accessInstructions: site?.accessInstructions ?? '',
        notes: site?.notes ?? '',
        technicalNotes: site?.technicalNotes ?? '',
        equipmentComposition: equipmentToString(site?.equipmentComposition),
      })
      setPendingFiles([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, site?.id])

  // Documenti esistenti (solo in edit mode)
  const { data: existingDocs = [], refetch: refetchDocs } = useQuery<SiteDocMeta[]>({
    queryKey: ['site-documents', site?.id],
    queryFn: async () => {
      const res = await api.get<SiteDocMeta[]>(`/sites/${site!.id}/documents`)
      return res.data
    },
    enabled: isEdit && Boolean(site?.id) && open,
  })

  const uploadFiles = useCallback(async (siteId: string, files: UploadedFile[]) => {
    if (files.length === 0) return
    const formData = new FormData()
    files.forEach((f) => formData.append('files', f.file))
    await api.post(`/sites/${siteId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }, [])

  const saveMutation = useMutation({
    mutationFn: async (data: SiteFormData) => {
      const payload = {
        ...data,
        companyId,
        equipmentComposition: data.equipmentComposition ? [{ description: data.equipmentComposition }] : [],
      }

      let siteId: string
      if (isEdit && site?.id) {
        await api.patch(`/sites/${site.id}`, payload)
        siteId = site.id
      } else {
        const res = await api.post<SiteData>('/sites', payload)
        siteId = res.data.id
      }

      await uploadFiles(siteId, pendingFiles)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sites', 'company', companyId] })
      setPendingFiles([])
      onClose()
    },
  })

  const deleteDoc = useMutation({
    mutationFn: async (docId: string) => {
      await api.delete(`/sites/${site!.id}/documents/${docId}`)
    },
    onSuccess: () => {
      void refetchDocs()
    },
  })

  const textareaClass =
    'w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Modifica sede: ${site?.name}` : 'Nuova sede'}
      size="xl"
    >
      <form
        onSubmit={handleSubmit((data) => saveMutation.mutate(data))}
        className="space-y-4"
      >
        {/* Info principali */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Nome sede *"
            {...register('name')}
            error={errors.name?.message}
            placeholder="es. Stabilimento Milano"
          />
          <Input
            label="Indirizzo *"
            {...register('address')}
            error={errors.address?.message}
            placeholder="Via Roma 1"
          />
          <Input
            label="Città"
            {...register('city')}
            placeholder="Milano"
          />
          <Input
            label="CAP"
            {...register('postalCode')}
            placeholder="20100"
          />
        </div>

        {/* Geolocalizzazione — automatica */}
        <div className="rounded-lg border border-[var(--fc-border)] p-3 space-y-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[var(--fc-primary)]" />
            <p className="text-sm font-medium text-[var(--fc-foreground)]">Geolocalizzazione automatica</p>
          </div>
          <p className="text-xs text-[var(--fc-foreground-muted)]">
            Le coordinate vengono calcolate automaticamente dall&apos;indirizzo al salvataggio.
          </p>
          {isEdit && site?.latitude != null && site?.longitude != null && (
            <p className="text-xs text-[var(--fc-foreground-muted)]">
              Coordinate attuali: {Number(site.latitude).toFixed(5)}, {Number(site.longitude).toFixed(5)}
            </p>
          )}
        </div>

        {/* Referente principale */}
        <div className="rounded-lg border border-[var(--fc-border)] p-3 space-y-3">
          <p className="text-sm font-medium text-[var(--fc-foreground)]">Referente principale</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Nome"
              {...register('contactName')}
              placeholder="Mario Rossi"
            />
            <Input
              label="Telefono"
              {...register('contactPhone')}
              placeholder="+39 348 0000000"
            />
            <Input
              label="Email"
              type="email"
              {...register('contactEmail')}
              error={errors.contactEmail?.message}
              placeholder="referente@azienda.it"
            />
          </div>
        </div>

        {/* Note */}
        <div className="grid grid-cols-1 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Note accesso</label>
            <textarea
              {...register('accessInstructions')}
              rows={2}
              className={textareaClass}
              placeholder="Istruzioni per l'accesso alla sede..."
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Note tecniche</label>
            <textarea
              {...register('technicalNotes')}
              rows={2}
              className={textareaClass}
              placeholder="Note tecniche specifiche della sede..."
            />
          </div>
        </div>

        {/* Composizione impianto + allegati */}
        <div className="rounded-lg border border-[var(--fc-border)] p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-[var(--fc-foreground-muted)]" />
            <p className="text-sm font-medium text-[var(--fc-foreground)]">Composizione impianto</p>
          </div>

          <div className="flex flex-col gap-1">
            <textarea
              {...register('equipmentComposition')}
              rows={3}
              className={textareaClass}
              placeholder="Descrivi liberamente l'impianto: marca, modello, tipo di apparecchiature..."
            />
          </div>

          {/* Allegati esistenti */}
          {isEdit && existingDocs.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-[var(--fc-foreground-muted)]">Documenti allegati</p>
              <ul className="space-y-1">
                {existingDocs.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center gap-2 rounded border border-[var(--fc-border)] bg-[var(--fc-surface-raised)] px-2 py-1.5"
                  >
                    <span className="flex-1 truncate text-xs text-[var(--fc-foreground)]">{doc.filename}</span>
                    <span className="shrink-0 text-xs text-[var(--fc-foreground-muted)]">
                      {(doc.size / 1024).toFixed(0)} KB
                    </span>
                    <a
                      href={`${api.defaults.baseURL}/sites/${site!.id}/documents/${doc.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)] transition-colors"
                      aria-label={`Scarica ${doc.filename}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    <button
                      type="button"
                      onClick={() => deleteDoc.mutate(doc.id)}
                      className="shrink-0 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-danger)] transition-colors"
                      aria-label={`Elimina ${doc.filename}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Upload nuovi file */}
          <FileUpload
            files={pendingFiles}
            onAdd={(files) => setPendingFiles((prev) => [...prev, ...files])}
            onRemove={(index) => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
            label="Aggiungi documenti (PDF, immagini)"
            accept={{
              'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
              'application/pdf': ['.pdf'],
            }}
          />
        </div>

        {/* Errore globale */}
        {saveMutation.isError && (
          <p className="text-sm text-[var(--fc-danger)]">
            Errore durante il salvataggio. Riprova.
          </p>
        )}

        {/* Azioni */}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={isSubmitting || saveMutation.isPending}>
            {saveMutation.isPending ? 'Salvataggio...' : isEdit ? 'Salva modifiche' : 'Crea sede'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
